import { WallPathShape, WallShape } from "../types";

/**
 * Project cursor position onto a wall segment and return the ratio (0-1)
 * along the wall where the cursor is closest.
 */
export const getCursorPositionOnWall = (
  cursor: { x: number; y: number },
  wall: WallShape
): number => {
  const dx = wall.x2 - wall.x;
  const dy = wall.y2 - wall.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) return 0.5;

  const t = ((cursor.x - wall.x) * dx + (cursor.y - wall.y) * dy) / lengthSq;
  return Math.max(0, Math.min(1, t));
};

/**
 * Find the closest edge of a wall-path to the cursor and return edgeIndex + position along that edge.
 */
export const getCursorPositionOnWallPath = (
  cursor: { x: number; y: number },
  path: WallPathShape
): { edgeIndex: number; position: number } | null => {
  const { vertices, closed } = path;
  const edgeCount = closed ? vertices.length : vertices.length - 1;
  if (edgeCount === 0) return null;

  let bestEdge = 0;
  let bestT = 0.5;
  let bestDistSq = Infinity;

  for (let i = 0; i < edgeCount; i++) {
    const from = vertices[i];
    const to = vertices[(i + 1) % vertices.length];
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const lengthSq = dx * dx + dy * dy;

    let t: number;
    if (lengthSq === 0) {
      t = 0.5;
    } else {
      t = Math.max(
        0,
        Math.min(
          1,
          ((cursor.x - from.x) * dx + (cursor.y - from.y) * dy) / lengthSq
        )
      );
    }

    const px = from.x + t * dx;
    const py = from.y + t * dy;
    const distSq = (cursor.x - px) ** 2 + (cursor.y - py) ** 2;

    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestEdge = i;
      bestT = t;
    }
  }

  return { edgeIndex: bestEdge, position: bestT };
};
